import React, { useState, useEffect, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  useTheme,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { useAuth } from "../../contexts/AuthContext";
import { useSnackbar } from "../../contexts/InfoContext";
import { SpinnerContext } from "../../contexts/SpinnerContext";
import { createBill, updateBill } from "../../api/bills/bills";
import getRequiredUserData from "../../utils/getBranchInfo";

const validationSchema = yup.object({
  date: yup.date().required("Date is required"),
  reason: yup.string().required("Reason is required"),
  type: yup.string().required("Type is required"),
  amount: yup
    .number()
    .typeError("Amount must be a number")
    .positive("Amount must be positive")
    .required("Amount is required"),
});

const BillForm = ({
  bill = null,
  isOpen,
  onClose,
  onSuccess,
  billTypes = [],
}) => {
  const theme = useTheme();
  const { user } = useAuth();
  const { openSnackbar } = useSnackbar();
  const { setIsSubmitting } = useContext(SpinnerContext);
  const userData = getRequiredUserData();

  const isEditing = !!bill;

  // Helper function to format Firestore timestamp to date string
  const formatTimestampToDateString = (timestamp) => {
    if (!timestamp) return new Date().toISOString().split("T")[0];

    // Handle Firestore timestamp
    if (timestamp.seconds) {
      const date = new Date(timestamp.seconds * 1000);
      return date.toISOString().split("T")[0];
    }

    // Handle regular Date object
    if (timestamp instanceof Date) {
      return timestamp.toISOString().split("T")[0];
    }

    // Handle date string
    if (typeof timestamp === "string") {
      return timestamp.split("T")[0];
    }

    return new Date().toISOString().split("T")[0];
  };

  const formik = useFormik({
    initialValues: {
      date: formatTimestampToDateString(bill?.date),
      reason: bill?.reason || "",
      type: bill?.type || "",
      amount: bill?.amount || "",
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        const billData = {
          branchId: userData.requiredId,
          salaryTableId: userData.active,
          date: new Date(values.date),
          reason: values.reason,
          type: values.type,
          amount: Number(values.amount),
        };

        if (isEditing) {
          await updateBill(user, bill.id, billData);
          openSnackbar("Bill updated successfully!", "success");
        } else {
          await createBill(user, billData);
          openSnackbar("Bill created successfully!", "success");
        }

        onSuccess();
        onClose();
        formik.resetForm();
      } catch (error) {
        if (error.response && error.response.data) {
          openSnackbar(
            error.response.data.message,
            error.response.data.type || "error"
          );
        } else {
          openSnackbar(
            "An unexpected error occurred. Please check your connection.",
            "error"
          );
        }
      }
      setIsSubmitting(false);
    },
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          backgroundColor: theme.palette.background.alt,
          color: theme.palette.secondary[200],
        },
      }}
    >
      <DialogTitle
        style={{
          color: theme.palette.secondary[100],
          backgroundColor: theme.palette.background.alt,
        }}
      >
        {isEditing ? "Edit Bill" : "Create New Bill"}
      </DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent
          style={{
            color: theme.palette.secondary[200],
            backgroundColor: theme.palette.background.alt,
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="date"
                name="date"
                label="Date"
                value={formik.values.date}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.date && Boolean(formik.errors.date)}
                helperText={formik.touched.date && formik.errors.date}
                InputLabelProps={{
                  shrink: true,
                  style: { color: theme.palette.secondary[100] },
                }}
                InputProps={{
                  style: { color: theme.palette.secondary[200] },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                name="reason"
                label="Reason"
                multiline
                rows={3}
                value={formik.values.reason}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.reason && Boolean(formik.errors.reason)}
                helperText={formik.touched.reason && formik.errors.reason}
                InputLabelProps={{
                  style: { color: theme.palette.secondary[100] },
                }}
                InputProps={{
                  style: { color: theme.palette.secondary[200] },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                name="type"
                label="Type"
                value={formik.values.type}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.type && Boolean(formik.errors.type)}
                helperText={formik.touched.type && formik.errors.type}
                InputLabelProps={{
                  style: { color: theme.palette.secondary[100] },
                }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      style: {
                        backgroundColor: theme.palette.background.alt,
                        color: theme.palette.secondary[200],
                      },
                    },
                  },
                }}
              >
                <MenuItem value="">Select Type</MenuItem>
                {billTypes.map((billType) => (
                  <MenuItem key={billType.id} value={billType.type}>
                    {billType.type}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                name="amount"
                label="Amount"
                value={formik.values.amount}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.amount && Boolean(formik.errors.amount)}
                helperText={formik.touched.amount && formik.errors.amount}
                InputLabelProps={{
                  style: { color: theme.palette.secondary[100] },
                }}
                InputProps={{
                  style: { color: theme.palette.secondary[200] },
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          style={{
            color: theme.palette.secondary[200],
            backgroundColor: theme.palette.background.alt,
          }}
        >
          <Button onClick={handleClose} color="inherit">
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={!formik.isValid || formik.isSubmitting}
          >
            {isEditing ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default BillForm;
